/**
 * Shared hook for AG-UI conversation persistence.
 *
 * Provides cache-first history loading, in-progress run detection,
 * streaming state tracking, and run cancellation for any AG-UI agent.
 *
 * Each ConversationRuntime instance is keyed on conversationId, so this
 * hook only ever serves one conversation per mount. Effects run once on
 * mount and clean up on unmount.
 *
 * Usage:
 *   const persistence = useAgUiPersistence({ agent, runtime, api, conversationId, ... });
 *   // → { isLoadingHistory, isRunningRemotely, isCancelling, onCancelRun }
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import type { HttpAgent } from "@ag-ui/client";
import type { AssistantRuntime, ThreadMessageLike } from "@assistant-ui/react";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import type { AssistantMessageCustomMetadata, TurnFeedback } from "~/components/assistant/types";
import { apiClient, isApiError } from "~/utils/apiClient";

// ---------------------------------------------------------------------------
// Module-level caches — persist for the SPA lifetime
// ---------------------------------------------------------------------------

const activeIds = new Map<string, string>();

// ---------------------------------------------------------------------------
// Running conversations tracker — reactive via useSyncExternalStore
// ---------------------------------------------------------------------------

let runningSnapshot = new Set<string>();
const runningListeners = new Set<() => void>();

function notifyRunningListeners() {
  runningListeners.forEach((l) => l());
}

function markConversationRunning(id: string) {
  if (!runningSnapshot.has(id)) {
    const next = new Set(runningSnapshot);
    next.add(id);
    runningSnapshot = next;
    notifyRunningListeners();
  }
}

function markConversationIdle(id: string) {
  if (runningSnapshot.has(id)) {
    const next = new Set(runningSnapshot);
    next.delete(id);
    runningSnapshot = next;
    notifyRunningListeners();
  }
}

function subscribeRunning(callback: () => void) {
  runningListeners.add(callback);
  return () => {
    runningListeners.delete(callback);
  };
}

function getRunningSnapshot() {
  return runningSnapshot;
}

/**
 * Reactive hook returning the set of conversation IDs with active runs.
 * Re-renders only when the set changes.
 */
export function useRunningConversations(): Set<string> {
  return useSyncExternalStore(subscribeRunning, getRunningSnapshot, getRunningSnapshot);
}

// ---------------------------------------------------------------------------
// History cache
// ---------------------------------------------------------------------------

interface HistoryCacheEntry {
  messages: ThreadMessageLike[];
  cachedAt: number;
}
const historyCache = new Map<string, HistoryCacheEntry>();
const MAX_CACHED = 10;

export function getCachedHistory(conversationId: string): ThreadMessageLike[] | null {
  return historyCache.get(conversationId)?.messages ?? null;
}

export function setCachedHistory(conversationId: string, messages: ThreadMessageLike[]): void {
  historyCache.set(conversationId, { messages, cachedAt: Date.now() });
  if (historyCache.size > MAX_CACHED) {
    // Map iteration order is insertion order — first key is oldest
    const oldest = historyCache.keys().next().value;
    if (oldest != null) historyCache.delete(oldest);
  }
}

// ---------------------------------------------------------------------------
// Active conversation tracking
// ---------------------------------------------------------------------------

/** Get the last-viewed conversation ID for a given context (e.g. dataset). */
export function getActiveConversationId(contextId: string): string | null {
  return activeIds.get(contextId) ?? null;
}

/** Remember which conversation was last viewed for a context. */
export function setActiveConversationId(contextId: string, conversationId: string | null): void {
  if (conversationId) {
    activeIds.set(contextId, conversationId);
  } else {
    activeIds.delete(contextId);
  }
}

// ---------------------------------------------------------------------------
// AG-UI → assistant-ui message conversion
// ---------------------------------------------------------------------------

interface AguiToolCall {
  id: string;
  type: string;
  function: { name: string; arguments: string };
}

interface AguiMessage {
  role: "user" | "assistant" | "tool";
  content: string | null;
  id?: string;
  toolCalls?: AguiToolCall[];
  toolCallId?: string;
  cancelled?: boolean;
  turnId?: string;
  turnFeedback?: TurnFeedback | null;
}

type ContentPart =
  | { readonly type: "text"; readonly text: string }
  | {
      readonly type: "tool-call";
      readonly toolCallId: string;
      readonly toolName: string;
      readonly args: Record<string, unknown>;
      readonly argsText: string;
      result?: string | null;
    };

/**
 * Merges consecutive assistant + tool messages into a single assistant
 * message per user turn, matching the structure assistant-ui expects.
 */
export function convertAguiToThreadMessages(aguiMessages: AguiMessage[]): ThreadMessageLike[] {
  const result: ThreadMessageLike[] = [];
  let currentParts: ContentPart[] = [];
  let lastTurnId: string | undefined;
  let lastTurnFeedback: TurnFeedback | null | undefined;
  let pendingCancelled = false;
  const flushAssistant = () => {
    if (currentParts.length > 0 || pendingCancelled) {
      const custom: AssistantMessageCustomMetadata = {};
      if (lastTurnId) {
        custom.turnId = lastTurnId;
      }
      if (lastTurnFeedback) {
        custom.feedback = lastTurnFeedback;
      }
      if (pendingCancelled) {
        custom.cancelled = true;
      }
      result.push({
        role: "assistant",
        content: currentParts as ThreadMessageLike["content"],
        metadata: Object.keys(custom).length > 0 ? { custom } : undefined,
      });
      currentParts = [];
      pendingCancelled = false;
    }
  };

  for (const msg of aguiMessages) {
    if (msg.role === "user") {
      flushAssistant();
      lastTurnId = msg.turnId;
      lastTurnFeedback = msg.turnFeedback;
      result.push({
        role: "user",
        content: msg.content ?? "",
        metadata: msg.turnId ? { custom: { turnId: msg.turnId } } : undefined,
      });
    } else if (msg.role === "assistant") {
      if (msg.cancelled) {
        pendingCancelled = true;
        continue;
      }
      if (msg.toolCalls) {
        for (const tc of msg.toolCalls) {
          let args: Record<string, unknown> = {};
          try {
            args = JSON.parse(tc.function.arguments);
          } catch {
            // keep empty args if parse fails
          }
          currentParts.push({
            type: "tool-call",
            toolCallId: tc.id,
            toolName: tc.function.name,
            args,
            argsText: tc.function.arguments,
          });
        }
      }
      if (msg.content && !msg.toolCalls) {
        currentParts.push({ type: "text", text: msg.content });
      }
    } else if (msg.role === "tool") {
      for (let i = currentParts.length - 1; i >= 0; i--) {
        const part = currentParts[i];
        if (part.type === "tool-call" && part.toolCallId === msg.toolCallId) {
          part.result = msg.content;
          break;
        }
      }
    }
  }

  flushAssistant();
  return result;
}

export async function fetchConversationHistory(
  baseUrl: string,
  conversationId: string
): Promise<ThreadMessageLike[]> {
  try {
    const resp = await apiClient<{ state: unknown; messages: AguiMessage[] }>(
      `${baseUrl}/conversations/${conversationId}/connect`
    );
    return resp.messages.length ? convertAguiToThreadMessages(resp.messages) : [];
  } catch (error) {
    if (isApiError(error) && error.status === 400) {
      return [];
    }
    throw error;
  }
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 2000;

function isInProgress(status: string | null | undefined): boolean {
  return status === "MESSAGE_IN_PROGRESS" || status === "MESSAGE_CANCELLED";
}

interface UseAgUiPersistenceOptions {
  agent: HttpAgent;
  runtime: AssistantRuntime;
  api: AssistantApi;
  conversationId: string;
  clearError?: () => void;
  /** Pre-converted messages from /connect — skips the /messages_agui fetch. */
  initialMessages?: ThreadMessageLike[] | null;
}

interface UseAgUiPersistenceReturn {
  isLoadingHistory: boolean;
  /** True after history loading completes and at least one message was found. */
  historyHasMessages: boolean;
  isRunningRemotely: boolean;
  isCancelling: boolean;
  onCancelRun: () => void;
}

/**
 * AG-UI conversation persistence hook.
 *
 * Each instance is keyed on conversationId (via the parent's React key),
 * so this hook only serves one conversation per mount lifecycle.
 */
export function useAgUiPersistence({
  agent,
  runtime,
  api,
  conversationId,
  clearError,
  initialMessages,
}: UseAgUiPersistenceOptions): UseAgUiPersistenceReturn {
  const { baseUrl } = api;
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [historyHasMessages, setHistoryHasMessages] = useState(false);
  const [isRunningRemotely, setIsRunningRemotely] = useState(() =>
    runningSnapshot.has(conversationId)
  );
  const [isCancelling, setIsCancelling] = useState(false);

  const abortPollingRef = useRef<(() => void) | null>(null);

  // --- Shared helper: fetch history from server → cache + reset thread ---

  const reloadHistory = useCallback(
    async (cid: string) => {
      try {
        const messages = await fetchConversationHistory(baseUrl, cid);
        if (messages.length > 0) {
          setCachedHistory(cid, messages);
          runtime.thread.reset(messages);
        }
      } catch (error) {
        console.error("Failed to reload history:", error);
      }
    },
    [baseUrl, runtime]
  );

  // --- Poll server status until run completes, then fetch result once ---

  const pollUntilReady = useCallback(
    (cid: string) => {
      abortPollingRef.current?.();
      let aborted = false;
      abortPollingRef.current = () => {
        aborted = true;
      };

      setIsRunningRemotely(true);
      markConversationRunning(cid);

      (async () => {
        while (!aborted) {
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          if (aborted) {
            return;
          }

          try {
            const resp = await api.getConversationStatus(cid);
            if (aborted) {
              return;
            }

            if (!isInProgress(resp.status)) {
              setIsRunningRemotely(false);
              setIsCancelling(false);
              markConversationIdle(cid);
              await reloadHistory(cid);
              return;
            }
          } catch (error) {
            console.error("Failed to poll status:", error);
          }
        }
      })();
    },
    [api, reloadHistory]
  );

  // --- 1. Load history on mount ---

  useEffect(() => {
    let active = true;

    (async () => {
      let loaded = false;

      // Prefer pre-fetched messages from /connect
      if (initialMessages && initialMessages.length > 0) {
        setCachedHistory(conversationId, initialMessages);
        runtime.thread.reset(initialMessages);
        loaded = true;
      } else if (!initialMessages) {
        // /connect had no messages or errored — try cache, then server
        const cached = getCachedHistory(conversationId);
        if (cached?.length) {
          runtime.thread.reset(cached);
          loaded = true;
        } else {
          try {
            const messages = await fetchConversationHistory(baseUrl, conversationId);
            if (!active) return;
            if (messages.length > 0) {
              setCachedHistory(conversationId, messages);
              runtime.thread.reset(messages);
              loaded = true;
            }
          } catch (error) {
            console.error("Failed to load conversation history:", error);
          }
        }
      }
      // initialMessages === [] means /connect returned no history — nothing to load

      if (active) {
        setHistoryHasMessages(loaded);
        setIsLoadingHistory(false);
      }
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  // --- 2. Detect in-progress runs on mount ---
  // Always checks backend status — one call per conversation mount is
  // acceptable overhead, and the alternative (gating on module-level
  // runningSnapshot) breaks after page refresh.

  useEffect(() => {
    // Register with abortPollingRef so that onRunStartedEvent (Effect 3)
    // can cancel this in-flight status check. Without this, a late-arriving
    // /status response would call pollUntilReady and set isRunningRemotely
    // even though a local SSE stream is already active.
    abortPollingRef.current?.();
    let cancelled = false;
    abortPollingRef.current = () => {
      cancelled = true;
    };

    (async () => {
      try {
        const resp = await api.getConversationStatus(conversationId);
        if (cancelled) return;
        if (isInProgress(resp.status)) {
          pollUntilReady(conversationId);
        } else {
          markConversationIdle(conversationId);
          setIsRunningRemotely(false);
          reloadHistory(conversationId);
        }
      } catch (error) {
        console.error("Failed to fetch conversation status:", error);
      }
    })();

    return () => {
      cancelled = true;
      abortPollingRef.current?.();
      abortPollingRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  // --- 3. Track local streaming + sync cache after run completes ---

  useEffect(() => {
    const cid = conversationId;
    const sub = agent.subscribe({
      onRunStartedEvent: () => {
        // Local stream supersedes remote detection — clear the
        // thread-level "Thinking" indicator and stop polling.
        setIsRunningRemotely(false);
        abortPollingRef.current?.();
        markConversationRunning(cid);
        const currentMessages = runtime.thread.getState().messages;
        if (currentMessages.length > 0) {
          setCachedHistory(cid, [...currentMessages.filter((m) => m.status?.type !== "running")]);
        }
      },
      onRunFinishedEvent: () => {
        markConversationIdle(cid);
      },
      onRunFailed: () => {
        markConversationIdle(cid);
      },
    });

    return () => sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  // --- 4. Cancel handler ---

  const onCancelRun = useCallback(() => {
    clearError?.();
    agent.abortRun();
    setIsCancelling(true);

    api.cancelMessage(conversationId).then(
      async () => {
        try {
          const resp = await api.getConversationStatus(conversationId);
          if (!isInProgress(resp.status)) {
            setIsCancelling(false);
            setIsRunningRemotely(false);
            markConversationIdle(conversationId);
            await reloadHistory(conversationId);
          } else {
            pollUntilReady(conversationId);
          }
        } catch {
          setIsCancelling(false);
          setIsRunningRemotely(false);
          markConversationIdle(conversationId);
        }
      },
      (error) => {
        console.error("Failed to cancel run:", error);
        setIsCancelling(false);
        setIsRunningRemotely(false);
        markConversationIdle(conversationId);
      }
    );
  }, [api, agent, conversationId, clearError, reloadHistory, pollUntilReady]);

  return {
    isLoadingHistory,
    historyHasMessages,
    isRunningRemotely,
    isCancelling,
    onCancelRun,
  };
}
